import { useEffect, useRef, useState } from "react";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type PercentCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { ErrorText, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { imageCropping } from "@/lib/imageCropping";

const FULL_IMAGE_PERCENT = 100;
const SQUARE_ASPECT_RATIO = 1;
const fullImageCrop: PercentCrop = {
  unit: "%",
  x: 0,
  y: 0,
  width: FULL_IMAGE_PERCENT,
  height: FULL_IMAGE_PERCENT,
};

export const CompanyLogoCropDialog = ({
  file,
  isSmallLogo,
  isPending,
  isError,
  onSave,
  onClose,
}: {
  file: File;
  isSmallLogo: boolean;
  isPending: boolean;
  isError: boolean;
  onSave: (file: File) => void;
  onClose: () => void;
}) => {
  /* React Hooks */
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageUrl, setImageUrl] = useState<string>();
  const [crop, setCrop] = useState<PercentCrop>(fullImageCrop);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string>();
  const [isImageReady, setIsImageReady] = useState(false);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);
  const isSaving = isPending || isExporting;

  /* Handlers */
  const handleImageLoad = () => {
    const image = imageRef.current;
    if (!image) return;
    if (isSmallLogo) {
      setCrop(
        centerCrop(
          makeAspectCrop(
            { unit: "%", width: FULL_IMAGE_PERCENT },
            SQUARE_ASPECT_RATIO,
            image.width,
            image.height,
          ),
          image.width,
          image.height,
        ),
      );
    }
    setIsImageReady(true);
  };
  const handleSave = async () => {
    if (!imageRef.current) return;
    setIsExporting(true);
    setError(undefined);
    try {
      const croppedFile = await imageCropping.crop(
        imageRef.current,
        crop,
        file.name,
      );
      onSave(croppedFile);
    } catch {
      setError("Could not crop this image. Please choose another image.");
    } finally {
      setIsExporting(false);
    }
  };
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isSaving) onClose();
  };

  /* Render */
  const render = () => (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isSmallLogo ? "Crop small logo" : "Crop logo"}
          </DialogTitle>
        </DialogHeader>
        <Text className="text-sm font-normal text-slate-500">
          Drag the crop area or its corners to adjust your logo.
        </Text>
        <Row className="min-h-32 items-center justify-center overflow-hidden rounded-lg bg-slate-100 p-3">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => {
              setCrop(percentCrop);
            }}
            aspect={isSmallLogo ? SQUARE_ASPECT_RATIO : undefined}
            keepSelection
            disabled={isSaving}
          >
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Logo to crop"
              className="max-h-[50vh] max-w-full object-contain"
              onLoad={handleImageLoad}
              onError={() => {
                setError(
                  "Could not open this image. Please choose another image.",
                );
              }}
            />
          </ReactCrop>
        </Row>
        {error ? <ErrorText errorMessage={error} /> : null}
        {isError ? (
          <ErrorText errorMessage="Could not upload the logo. Please try again." />
        ) : null}
        <Row className="justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSaving || !isImageReady || !crop.width || !crop.height}
            onClick={() => {
              void handleSave();
            }}
          >
            {isSaving ? "Uploading…" : "Crop & upload"}
          </Button>
        </Row>
      </DialogContent>
    </Dialog>
  );
  return render();
};
